import React, { useState } from 'react';
import { Box, Avatar, IconButton, CircularProgress } from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import api from '../config/api';

const ProfilePicture = ({ currentUrl, onUpdate, userId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      setError('File must be an image');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create a FormData object for multipart/form-data request
      const formData = new FormData();
      formData.append('profilePic', file);
      
      // Add userId (which is actually email) to the form data
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      // Use the provided userId (email) or fall back to user.email from localStorage
      formData.append('userId', userId || user.email);

      // Make the request to upload the profile picture
      const response = await api.post('/auth/upload-profile-pic', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        // Update the profile picture URL in the parent component
        onUpdate(response.data.filePath);
        
        // Update localStorage
        if (user && user.email) {
          user.profile_picture_url = response.data.filePath;
          localStorage.setItem('user', JSON.stringify(user));
          
          // Dispatch an event to notify other components (like Header) that user data changed
          window.dispatchEvent(new Event('userDataChanged'));
        }
      } else {
        setError('Failed to upload image');
      }
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      setError(err.response?.data?.message || 'Failed to upload image');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveProfilePicture = async () => {
    if (!currentUrl) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Get user ID (email) from props or localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userIdToUse = userId || user.email;
      
      const response = await api.post('/auth/remove-profile-picture', { userId: userIdToUse });
      
      if (response.data.success) {
        // Update the profile picture URL in the parent component
        onUpdate(null);
        
        // Update localStorage
        if (user && user.email) {
          user.profile_picture_url = null;
          localStorage.setItem('user', JSON.stringify(user));
          
          // Dispatch an event to notify other components (like Header) that user data changed
          window.dispatchEvent(new Event('userDataChanged'));
        }
      } else {
        setError('Failed to remove profile picture');
      }
    } catch (err) {
      console.error('Error removing profile picture:', err);
      setError(err.response?.data?.message || 'Failed to remove profile picture');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <Avatar
        src={currentUrl}
        sx={{ width: 100, height: 100 }}
      />
      <input
        accept="image/*"
        style={{ display: 'none' }}
        id="profile-picture-upload"
        type="file"
        onChange={handleImageUpload}
      />
      <label htmlFor="profile-picture-upload">
        <IconButton
          color="primary"
          component="span"
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            backgroundColor: 'white',
            '&:hover': { backgroundColor: 'white' }
          }}
        >
          {loading ? <CircularProgress size={24} /> : <PhotoCamera />}
        </IconButton>
      </label>
      {currentUrl && (
        <IconButton
          color="secondary"
          onClick={handleRemoveProfilePicture}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            backgroundColor: 'rgba(255,255,255,0.7)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
            fontSize: '1rem',
            width: 24,
            height: 24,
            '& .MuiSvgIcon-root': { fontSize: '1rem' }
          }}
        >
          ×
        </IconButton>
      )}
      {error && (
        <Box sx={{ color: 'error.main', mt: 1, fontSize: '0.875rem' }}>
          {error}
        </Box>
      )}
    </Box>
  );
};

export default ProfilePicture; 